import { toast } from '@/components/ui/toaster'
import {
  EnrichmentOutputData,
  StructuredOutput,
  useTurnStatusQuery,
  type TurnStatus,
} from '@/hooks/api/use-turn-status-query'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { EnrichmentMetadataType, EnrichmentType } from '../constants'
import { EnrichmentParams, EnrichmentWorkflowState } from '../types'
import { normalizeEnrichedTable } from '../utils'

interface UseEnrichmentBaseOptions {
  type: EnrichmentType
  identifier: string
  mutationFn: (params: EnrichmentParams & { schema_name: string }) => Promise<any>
  originalData?: any
  schemaName?: string
}

export const useEnrichmentBase = ({
  type,
  identifier,
  mutationFn,
  originalData,
  schemaName,
}: UseEnrichmentBaseOptions) => {
  const [state, setState] = useState<EnrichmentWorkflowState>({
    workflowId: null,
    turnId: null,
    schemaName: null,
    isPolling: false,
    enrichmentData: null,
    originalData: originalData || null,
    currentView: 'enriched',
    selectedTableIds: [],
    selectedMetadata: [],
    enrichmentState: 'idle',
  })

  const enrichMutation = useMutation({
    mutationFn,
  })

  const getStorageKey = (identifier: string) => {
    if (type === 'table') {
      // For table enrichment: 'table_enrich_{schemaName}'
      return `table_enrich_${identifier}`
    } else {
      // For column enrichment: 'column_enrich_{schemaName}_{tableName}'
      return `column_enrich_${identifier}`
    }
  }

  const saveWorkflowToStorage = (
    identifier: string,
    workflowId: string,
    turnId: string,
    selectedTableIds: string[],
    selectedMetadata: EnrichmentMetadataType[],
  ) => {
    try {
      const storageData = {
        workflowId,
        turnId,
        selectedTableIds: type === 'table' ? selectedTableIds : undefined,
        selectedMetadata,
        timestamp: Date.now(),
      }
      localStorage.setItem(getStorageKey(identifier), JSON.stringify(storageData))
    } catch (error) {
      console.error('Failed to save workflow to storage:', error)
    }
  }

  const getWorkflowFromStorage = (
    identifier: string,
  ): {
    workflowId: string
    turnId: string
    selectedTableIds: string[]
    selectedMetadata: EnrichmentMetadataType[]
  } | null => {
    try {
      const stored = localStorage.getItem(getStorageKey(identifier))
      if (stored) {
        const parsed = JSON.parse(stored)
        return {
          workflowId: parsed.workflowId,
          turnId: parsed.turnId,
          selectedTableIds: parsed.selectedTableIds || [],
          selectedMetadata: parsed.selectedMetadata || [],
        }
      }
      return null
    } catch (error) {
      console.error('Failed to get workflow from storage:', error)
      return null
    }
  }

  const clearWorkflowFromStorage = (identifier: string) => {
    try {
      localStorage.removeItem(getStorageKey(identifier))
    } catch (error) {
      console.error('Failed to clear workflow from storage:', error)
    }
  }

  const mergeEnrichedData = (
    enrichedTables: any[],
    originalTables: any[],
    enrichedTableNames: string[],
  ) => {
    if (!originalTables || !enrichedTables) return enrichedTables

    // Normalize enriched tables first
    const normalizedEnrichedTables = enrichedTables.map(normalizeEnrichedTable)

    // Create a map of enriched tables by table name
    const enrichedTableMap = new Map()
    for (const table of normalizedEnrichedTables) {
      const tableName = table.table_name || table.tableName
      if (tableName) {
        enrichedTableMap.set(tableName, table)
      }
    }

    return originalTables.map((originalTable) => {
      const tableName = originalTable.table_name || originalTable.tableName
      const enrichedTable = enrichedTableMap.get(tableName)

      // If no enriched data for this table, return original
      if (!enrichedTable) {
        return { ...originalTable, _isEnriched: false }
      }

      // For table enrichment: only merge if table was selected
      if (type === 'table' && !enrichedTableNames.includes(tableName)) {
        return { ...originalTable, _isEnriched: false }
      }

      // Merge table-level data
      const mergedTable = {
        ...originalTable,
        table_description: enrichedTable.tableDescription || '',
        tags: enrichedTable.tags || {},
        table_domain: enrichedTable.tableDomain || '',
        _isEnriched: true,
      }

      // For column enrichment: merge column data
      if (type === 'column' && enrichedTable.columns && Array.isArray(enrichedTable.columns)) {
        const enrichedColumnMap = new Map()
        enrichedTable.columns.forEach((col: any) => {
          const columnName = col.columnName || col.column_name
          if (columnName) {
            enrichedColumnMap.set(columnName, col)
          }
        })

        mergedTable.columns = (originalTable.columns || []).map((originalCol: any) => {
          const columnName = originalCol.column_name || originalCol.columnName
          const enrichedCol = enrichedColumnMap.get(columnName)

          if (enrichedCol) {
            return {
              ...originalCol,
              column_description: enrichedCol.columnDescription || '',
              business_definition: enrichedCol.businessDefinition || '',
              business_rules: enrichedCol.businessRules || '',
              tags: enrichedCol.tags || {},
              _isEnriched: true,
            }
          }

          return { ...originalCol, _isEnriched: false }
        })
      }

      return mergedTable
    })
  }

  // Check for existing workflow on mount and restore state
  useEffect(() => {
    if (identifier) {
      const stored = getWorkflowFromStorage(identifier)
      if (stored) {
        setState((prev) => ({
          ...prev,
          workflowId: stored.workflowId,
          turnId: stored.turnId,
          schemaName: identifier,
          isPolling: true,
          selectedTableIds: stored.selectedTableIds || [],
          selectedMetadata: stored.selectedMetadata || [],
          enrichmentState: 'enriching', // Set to enriching if we have stored workflow
        }))
      } else {
        // If no stored workflow, initialize with all tables selected for table enrichment
        if (type === 'table' && originalData?.tables) {
          const allTableIds = originalData.tables
            .map((table: any) => table.table_name)
            .filter(Boolean)
          setState((prev) => ({
            ...prev,
            schemaName: identifier,
            selectedTableIds: allTableIds,
          }))
        }
      }
    }
  }, [identifier, type, originalData])

  // Update originalData if it's null and we have the prop
  useEffect(() => {
    if (originalData && !state.originalData) {
      setState((prev) => ({
        ...prev,
        originalData: originalData,
      }))
    }
  }, [originalData, state.originalData])

  // Poll turn status when we have workflowId and turnId
  const {
    data: turnData,
    isLoading: isTurnLoading,
    error: turnError,
  } = useTurnStatusQuery({
    workflowId: state.workflowId || '',
    turnId: state.turnId || '',
    queryOptions: {
      enabled: Boolean(state.workflowId && state.turnId),
      refetchInterval: state.isPolling ? 2000 : false,
      staleTime: 0,
    },
  })

  // Stop polling if there's an error from the API
  useEffect(() => {
    if (turnError && state.isPolling) {
      console.error('Turn status query error:', turnError)
      setState((prev) => ({
        ...prev,
        isPolling: false,
        enrichmentState: 'idle', // Reset to idle on error
      }))
      // Clear local storage on error
      clearWorkflowFromStorage(identifier)
      toast.error('Failed to get turn status')
    }
  }, [turnError, state.isPolling, identifier])

  // Handle turn status changes
  useEffect(() => {
    if (!turnData || !state.workflowId || !state.turnId) return
    if (type === 'table' && !state.selectedTableIds?.length) return

    const status = turnData.status as TurnStatus

    if (status === 'SUCCESS') {
      handleSuccessfulEnrichment(turnData)
    } else if (status === 'FAILURE' || status === 'CANCELLED') {
      handleFailedEnrichment(status)
    }
  }, [turnData, type])

  const handleSuccessfulEnrichment = (turnData: any) => {
    setState((prev) => ({
      ...prev,
      isPolling: false,
      enrichmentState: 'enriched', // Set to enriched when successful
    }))

    try {
      const output = turnData.output
      if (!isValidOutput(output)) {
        handleInvalidOutput(output)
        return
      }

      const structuredOutput = output as StructuredOutput[]
      const outputData = structuredOutput[0]?.data

      if (!isValidOutputData(outputData)) {
        handleInvalidOutputData(outputData)
        return
      }

      const enrichedTableNames = state.selectedTableIds || []
      const enrichmentData = createEnrichmentData(
        outputData as EnrichmentOutputData,
        enrichedTableNames,
      )

      setState((prev) => ({
        ...prev,
        enrichmentData: enrichmentData,
        currentView: 'enriched', // Ensure we show enriched data view when enrichment completes
        isPolling: false,
        enrichmentState: 'enriched', // Keep as enriched
      }))

      const successMessage =
        outputData.operation_status?.message || 'Enrichment completed successfully'
      toast.success(successMessage)
    } catch (error) {
      handleParseError(error)
    }
  }

  const isValidOutput = (output: any): boolean => {
    return output && Array.isArray(output) && output.length > 0
  }

  const isValidOutputData = (outputData: any): boolean => {
    return outputData?.tables && Array.isArray(outputData.tables)
  }

  const createEnrichmentData = (outputData: any, enrichedTableNames: string[]) => {
    const currentOriginalData = state.originalData

    // If we have original data, merge it with enriched data
    if (currentOriginalData?.tables && Array.isArray(currentOriginalData.tables)) {
      const mergedTables = mergeEnrichedData(
        outputData.tables,
        currentOriginalData.tables,
        enrichedTableNames,
      )

      return {
        ...outputData,
        tables: mergedTables,
        enrichedTableNames,
      }
    }

    // If no original data, return enriched data as-is
    return {
      ...outputData,
      tables: outputData.tables,
      enrichedTableNames,
    }
  }

  const handleInvalidOutput = (output: any) => {
    let errorMsg = 'Invalid workflow output format'
    if (!output) {
      errorMsg = 'No output data received from workflow'
    } else if (!Array.isArray(output)) {
      errorMsg = 'Workflow output is not in expected array format'
    } else if (output.length === 0) {
      errorMsg = 'Workflow output array is empty'
    }

    console.error('Invalid output format:', { output, isArray: Array.isArray(output) })
    setState((prev) => ({
      ...prev,
      isPolling: false,
      enrichmentState: 'idle', // Reset to idle on invalid output
    }))
    clearWorkflowFromStorage(identifier)
    toast.error(errorMsg)
  }

  const handleInvalidOutputData = (outputData: any) => {
    let errorMsg = 'No enrichment data found in workflow output'
    if (!outputData) {
      errorMsg = 'No data found in workflow output'
    } else if (!outputData.tables) {
      errorMsg = 'No tables found in workflow output data'
    } else if (!Array.isArray(outputData.tables)) {
      errorMsg = 'Tables data is not in expected array format'
    }

    console.error('Error details:', { outputData, tables: outputData?.tables })
    setState((prev) => ({
      ...prev,
      isPolling: false,
      enrichmentState: 'idle',
    }))
    toast.error(errorMsg)
  }

  const handleParseError = (error: any) => {
    const errorMsg = 'Failed to parse enrichment data from workflow output'
    console.error('Parse error:', error)
    setState((prev) => ({
      ...prev,
      isPolling: false,
      enrichmentState: 'idle',
    }))
    toast.error(errorMsg)
  }

  const handleFailedEnrichment = (status: string) => {
    const errorMsg = `Workflow ${status.toLowerCase()}`
    setState((prev) => ({
      ...prev,
      isPolling: false,
      enrichmentState: 'idle',
    }))
    toast.error(errorMsg)
    clearWorkflowFromStorage(identifier)
  }

  const startEnrichment = useCallback(
    async (params: EnrichmentParams, selectedMetadata: EnrichmentMetadataType[] = []) => {
      // Safety check - don't start enrichment if no identifier
      if (!identifier) {
        console.warn('Cannot start enrichment: no identifier provided')
        return
      }

      setState((prev) => ({
        ...prev,
        originalData: originalData || prev.originalData, // Store original data when starting enrichment
        currentView: 'enriched', // Reset to enriched view when starting new enrichment
        selectedTableIds: params.tables || [],
        selectedMetadata: selectedMetadata || [],
        enrichmentState: 'enriching', // Set to enriching when starting
      }))

      enrichMutation.mutate(
        {
          ...params,
          schema_name: schemaName || identifier,
        },
        {
          onSuccess: (response) => {
            setState((prev) => ({
              ...prev,
              workflowId: response.workflowId,
              turnId: response.turnId,
              schemaName: identifier,
              isPolling: true,
              enrichmentState: 'enriching', // Keep as enriching while polling
            }))
            saveWorkflowToStorage(
              identifier,
              response.workflowId,
              response.turnId,
              params.tables || [],
              state.selectedMetadata || [],
            )
            toast.success('Enrichment workflow started successfully')
          },
          onError: (error) => {
            let errorMsg = 'Failed to start enrichment'
            setState((prev) => ({
              ...prev,
              isPolling: false,
              workflowId: null,
              turnId: null,
              enrichmentState: 'idle', // Reset to idle on error
            }))
            // Clear local storage on error
            clearWorkflowFromStorage(identifier)
            if (error instanceof Error && error.message) {
              errorMsg = error.message
            }
            toast.error(errorMsg)
          },
        },
      )
    },
    [identifier, schemaName, enrichMutation],
  )

  const switchToOriginalView = () => {
    setState((prev) => ({
      ...prev,
      currentView: 'original',
    }))
  }

  const switchToEnrichedView = () => {
    setState((prev) => ({
      ...prev,
      currentView: 'enriched',
    }))
  }

  const clearAllData = () => {
    clearWorkflowFromStorage(identifier)

    setState({
      workflowId: null,
      turnId: null,
      schemaName: null,
      isPolling: false,
      enrichmentData: null,
      originalData: null,
      currentView: 'enriched',
      selectedTableIds: [],
      selectedMetadata: [],
      enrichmentState: 'idle',
    })
  }

  const updateSelectedTableIds = (newSelectedTableIds: string[]) => {
    setState((prev) => ({
      ...prev,
      selectedTableIds: newSelectedTableIds,
    }))
  }

  const updateSelectedMetadata = (newSelectedMetadata: EnrichmentMetadataType[]) => {
    setState((prev) => ({
      ...prev,
      selectedMetadata: newSelectedMetadata || [],
    }))
  }

  const isEnrichmentInProcess = state.enrichmentState === 'enriching'
  const isEnrichmentCompleted = state.enrichmentState === 'enriched'

  // Extract latest update from turn data
  const getLatestUpdate = () => {
    if (!turnData?.steps || !Array.isArray(turnData.steps) || turnData.steps.length === 0) {
      return null
    }

    // considering there will be only one step for enrichment workflows
    const step = turnData.steps[0]
    if (!step?.tasks || !Array.isArray(step.tasks) || step.tasks.length === 0) {
      return null
    }

    // considering there will be only one task for enrichment workflows
    const task = step.tasks[0]
    if (!task?.updates || !Array.isArray(task.updates) || task.updates.length === 0) {
      return null
    }

    // Find the update with the highest updateTimestamp
    const latestUpdate = task.updates.reduce((latest, current) => {
      const latestTimestamp = latest?.updateTimestamp || 0
      const currentTimestamp = current?.updateTimestamp || 0
      return currentTimestamp > latestTimestamp ? current : latest
    })

    const content = latestUpdate?.output?.content

    // Only return string content, not LiveSessionUpdateContent objects
    return typeof content === 'string' ? content : null
  }

  const latestUpdate = getLatestUpdate()

  return {
    state,
    startEnrichment,
    clearAllData,
    switchToOriginalView,
    switchToEnrichedView,
    updateSelectedTableIds,
    updateSelectedMetadata,
    isTurnLoading,
    isEnrichmentInProcess,
    isEnrichmentCompleted,
    latestUpdate,
  }
}
